import * as React from 'react'
import {
  Typography,
  TextField,
  Grid,
  Box,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  Link,
  Grow,
  FormHelperText,
} from '@mui/material'
import {
  Language,
  Videocam,
  Instagram,
  Facebook,
  PhoneAndroid,
  LocalPhone,
} from '@mui/icons-material'
import {
  Controller,
  Control,
  useFormState,
  UseFormGetValues,
  UseFormSetValue,
  Path,
  useWatch,
  UseFormResetField,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { SOURCE_OPTIONS, SOURCES } from 'helpers/constants'
import Images from './Images'
import Partners from './Partners'
import { FormValues } from './'
import { SourceEnum } from 'types/enums'
import AgentService, { useEstateAgents } from 'api/services/Agent'


interface Step2Props {
  control: Control<FormValues>
  setValue: UseFormSetValue<FormValues>
  getValues: UseFormGetValues<FormValues>
  resetField: UseFormResetField<FormValues>
}

interface ExternalProps {
  control: Control<FormValues>
  getValues: UseFormGetValues<FormValues>
  resetField: UseFormResetField<FormValues>
}

function External(props: ExternalProps) {
  const { t } = useTranslation()
  const { control, getValues, resetField } = props

  const watchedSource = useWatch({
    control,
    name: 'estateAgentSystem',
  })

  React.useEffect(() => {
    if (
      !watchedSource ||
      ![
        SOURCES.ORBIT as typeof watchedSource,
        SOURCES.FORTUNET as typeof watchedSource,
      ].includes(watchedSource)
    ) {
      resetField('externalId', { defaultValue: '' })
    }
  }, [resetField, watchedSource])

  return (
    <>
      {[
        SOURCES.ORBIT as typeof watchedSource,
        SOURCES.FORTUNET as typeof watchedSource,
      ].includes(watchedSource) ? (
        <Grid item xs={12} sm={6}>
          <Controller
            name="externalId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                fullWidth
                variant="outlined"
                label={`${t(
                  'dashboard.pages.createAgent.company.fields.crmCode'
                )}*`}
                size="small"
                {...field}
                error={!!error}
                helperText={error ? error?.message : ' '}
              />
            )}
            rules={{
              validate: () => {
                if (
                  (getValues('estateAgentSystem') !== SOURCES.ORBIT ||
                    getValues('estateAgentSystem') !== SOURCES.FORTUNET) &&
                  getValues('externalId') === ''
                )
                  return false
              },
            }}
          />
        </Grid>
      ) : null}
    </>
  )
}

function Step2(props: Step2Props) {
  const { control, getValues, setValue, resetField } = props
  const { t } = useTranslation()

  const { errors } = useFormState({ control })

  return (
    <>
      <div>
        <Box
          sx={{
            bgcolor: 'background.paper',
            px: { xs: 2, sm: 2, md: 3, lg: 4, xl: 6 },
            pt: 2,
            pb: 4,
            borderRadius: 2.5,
            maxWidth: (theme) => theme.breakpoints.values.lg,
            mx: 'auto',
          }}
        >
          <Grid container rowSpacing={1} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" align="center">
                      {t('dashboard.pages.createAgent.contactInfo.label')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`companyTitle.text`}
                      rules={{
                        required: t(
                          'validation.fieldRequired'
                        ) as unknown as string,
                      }}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          fullWidth
                          variant="outlined"
                          label={`${t(
                            'dashboard.pages.createAgent.company.fields.companyTitle'
                          )}*`}
                          size="small"
                          error={!!error}
                          helperText={error ? error?.message : ' '}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`contactInfo.email`}
                      control={control}
                      rules={{
                        required: t(
                          'validation.fieldRequired'
                        ) as unknown as string,
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          fullWidth
                          variant="outlined"
                          label={`${t(
                            'dashboard.pages.createAgent.contactInfo.fields.email'
                          )}*`}
                          size="small"
                          {...field}
                          error={!!error}
                          helperText={error ? error?.message : ' '}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      rules={{
                        required: t(
                          'validation.fieldRequired'
                        ) as unknown as string,
                      }}
                      name="description"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          fullWidth
                          variant="outlined"
                          multiline
                          minRows={3}
                          label={`${t(
                            'dashboard.pages.createAgent.company.fields.description'
                          )}*`}
                          size="small"
                          error={!!error}
                          helperText={error ? error?.message : ' '}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      rules={{
                        required: t(
                          'validation.fieldRequired'
                        ) as unknown as string,
                      }}
                      name="comments"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          fullWidth
                          variant="outlined"
                          multiline
                          minRows={3}
                          label={`${t(
                            'dashboard.pages.createAgent.company.fields.comments'
                          )}*`}
                          size="small"
                          error={!!error}
                          helperText={error ? error?.message : ' '}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="estateAgentSystem"
                      control={control}
                      rules={{
                        required: t(
                          'validation.fieldRequired'
                        ) as unknown as string,
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          fullWidth
                          select
                          label={
                            t(
                              'dashboard.pages.createAgent.company.fields.crm'
                            ) + '*'
                          }
                          {...field}
                          size="small"
                          defaultValue=""
                          error={!!error}
                          helperText={error ? error?.message : ' '}
                          SelectProps={{
                            MenuProps: {
                              sx: {
                                '& .MuiButtonBase-root.MuiMenuItem-root.Mui-selected':
                                  {
                                    bgcolor: 'primary.main',
                                  },
                              },
                            },
                          }}
                        >
                          <MenuItem disabled value={''}>
                            {t('signUpPage.steps.step2.CRMoptions')}
                          </MenuItem>
                          {/* Hide Golden Home from Sign Up */}
                          {SOURCE_OPTIONS.filter(
                            (item) => item.value !== SourceEnum.GoldenHome
                          ).map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {t(item.label)}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>
                  <External
                    control={control}
                    getValues={getValues}
                    resetField={resetField}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12}>
                <Typography variant="body2" align="center">
                  {t(
                    'dashboard.pages.createAgent.contactInfo.fields.address.title'
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`address.city`}
                  control={control}
                  rules={{
                    required: t(
                      'validation.fieldRequired'
                    ) as unknown as string,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={`${t(
                        'dashboard.pages.createAgent.contactInfo.fields.address.city'
                      )}*`}
                      size="small"
                      {...field}
                      error={!!error}
                      helperText={error ? error?.message : ' '}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`address.area`}
                  control={control}
                  rules={{
                    required: t(
                      'validation.fieldRequired'
                    ) as unknown as string,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={`${t(
                        'dashboard.pages.createAgent.contactInfo.fields.address.area'
                      )}*`}
                      size="small"
                      {...field}
                      error={!!error}
                      helperText={error ? error?.message : ' '}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`address.street`}
                  control={control}
                  rules={{
                    required: t(
                      'validation.fieldRequired'
                    ) as unknown as string,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={`${t(
                        'dashboard.pages.createAgent.contactInfo.fields.address.street'
                      )}*`}
                      size="small"
                      {...field}
                      error={!!error}
                      helperText={error ? error?.message : ' '}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`address.streetNumber`}
                  control={control}
                  rules={{
                    required: t(
                      'validation.fieldRequired'
                    ) as unknown as string,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={`${t(
                        'dashboard.pages.createAgent.contactInfo.fields.address.streetNumber'
                      )}*`}
                      size="small"
                      {...field}
                      error={!!error}
                      helperText={error ? error?.message : ' '}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`address.postalCode`}
                  control={control}
                  rules={{
                    required: t(
                      'validation.fieldRequired'
                    ) as unknown as string,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={`${t(
                        'dashboard.pages.createAgent.contactInfo.fields.address.postalCode'
                      )}*`}
                      size="small"
                      {...field}
                      error={!!error}
                      helperText={error ? error?.message : ' '}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" align="center">
                    {t(
                      'dashboard.pages.createAgent.contactInfo.fields.telephones'
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`contactInfo.landLineNumber`}
                    control={control}
                    rules={{
                      required: false,
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        fullWidth
                        variant="outlined"
                        label={`${t(
                          'dashboard.pages.createAgent.contactInfo.fields.landLineNumber'
                        )} 1`}
                        size="small"
                        {...field}
                        error={!!error}
                        helperText={error ? error?.message : ' '}
                        InputProps={{
                          endAdornment: <LocalPhone color="primary" />,
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`contactInfo.landLineNumber2`}
                    control={control}
                    rules={{ required: false }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        fullWidth
                        variant="outlined"
                        label={`${t(
                          'dashboard.pages.createAgent.contactInfo.fields.landLineNumber'
                        )} 2`}
                        size="small"
                        {...field}
                        error={!!error}
                        helperText={error ? error?.message : ' '}
                        InputProps={{
                          endAdornment: <LocalPhone color="primary" />,
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`contactInfo.mobileNumber`}
                    control={control}
                    rules={{ required: false }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        fullWidth
                        variant="outlined"
                        label={`${t(
                          'dashboard.pages.createAgent.contactInfo.fields.mobileNumber'
                        )} 1`}
                        size="small"
                        {...field}
                        error={!!error}
                        helperText={error ? error?.message : ' '}
                        InputProps={{
                          endAdornment: <PhoneAndroid color="primary" />,
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`contactInfo.mobileNumber2`}
                    control={control}
                    rules={{ required: false }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        fullWidth
                        variant="outlined"
                        label={`${t(
                          'dashboard.pages.createAgent.contactInfo.fields.mobileNumber'
                        )} 2`}
                        size="small"
                        {...field}
                        error={!!error}
                        helperText={error ? error?.message : ' '}
                        InputProps={{
                          endAdornment: <PhoneAndroid color="primary" />,
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" align="center">
                  {t('dashboard.pages.createAgent.partners.label')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Partners
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                />
              </Grid>
            </Grid>
            <Grid item container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" align="center">
                  {t('dashboard.pages.createAgent.contactInfo.fields.socials')}
                </Typography>
              </Grid>
              {[
                {
                  formPath: 'socialMedia.Website.url',
                  labelPath:
                    'dashboard.pages.createAgent.contactInfo.fields.socialMediaInfo.website',
                  icon: <Language color="primary" />,
                },
                {
                  formPath: 'socialMedia.Facebook.url',
                  labelPath:
                    'dashboard.pages.createAgent.contactInfo.fields.socialMediaInfo.fb',
                  icon: <Facebook color="fb" />,
                },
                {
                  formPath: 'socialMedia.Instagram.url',
                  labelPath:
                    'dashboard.pages.createAgent.contactInfo.fields.socialMediaInfo.insta',
                  icon: <Instagram color="pinterest" />,
                },
                {
                  formPath: 'socialMedia.Video.url',
                  labelPath:
                    'dashboard.pages.createAgent.contactInfo.fields.socialMediaInfo.video',
                  icon: <Videocam color="primary" />,
                },
              ].map((item) => (
                <Grid item xs={12} sm={4} key={item.formPath}>
                  <Controller
                    name={item.formPath as Path<FormValues>}
                    control={control}
                    rules={{ required: false }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        fullWidth
                        variant="outlined"
                        label={`${t(item.labelPath)}`}
                        size="small"
                        {...field}
                        error={!!error}
                        helperText={error ? error?.message : ' '}
                        InputProps={{
                          endAdornment: item.icon,
                        }}
                      />
                    )}
                  />
                </Grid>
              ))}
            </Grid>
            <Grid item container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" align="center">
                  {t('dashboard.pages.createAgent.company.label')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name={`companyName.text` as const}
                  control={control}
                  rules={{
                    required: t(
                      'validation.fieldRequired'
                    ) as unknown as string,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={`${t(
                        'dashboard.pages.createAgent.company.fields.companyName'
                      )}*`}
                      size="small"
                      {...field}
                      error={!!error}
                      helperText={error ? error?.message : ' '}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <Controller
                  name={`vatNumber`}
                  control={control}
                  rules={{
                    required: t(
                      'validation.fieldRequired'
                    ) as unknown as string,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={`${t(
                        'dashboard.pages.createAgent.company.fields.vatNumber'
                      )}*`}
                      size="small"
                      {...field}
                      error={!!error}
                      helperText={error ? error?.message : ' '}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <Controller
                  name={`companyCommercialId`}
                  control={control}
                  rules={{
                    required: t(
                      'validation.fieldRequired'
                    ) as unknown as string,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={`${t(
                        'dashboard.pages.createAgent.company.fields.companyCommercialId'
                      )}*`}
                      size="small"
                      {...field}
                      error={!!error}
                      helperText={error ? error?.message : ' '}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" align="center">
                {t('dashboard.pages.createAgent.images.label')}
              </Typography>
              <Images
                errors={errors}
                control={control}
                setValue={setValue}
                getValues={getValues}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="termsAgreement"
                rules={{
                  required: t(
                    'signUpPage.acceptTermsCheckbox.error'
                  ) as unknown as string,
                }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <FormControl error={!!error}>
                    <FormControlLabel
                      control={<Checkbox onChange={onChange} checked={value} />}
                      label={
                        <Box>
                          <Typography>
                            {t('signUpPage.acceptTermsCheckbox.text')}
                            <Link
                              href={`${process.env.REACT_APP_REDIRECT_SEARCH_APP_URL}terms`}
                              variant="body1"
                              underline="none"
                              target="_blank"
                            >
                              {t('signUpPage.acceptTermsCheckbox.link')}
                            </Link>
                          </Typography>
                        </Box>
                      }
                    />
                    <Grow in={Boolean(error)}>
                      <FormHelperText error={Boolean(error)}>
                        {error?.message}
                      </FormHelperText>
                    </Grow>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </div>
    </>
  )
}

export default React.memo(Step2)
